import { useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ChevronLeft, X } from 'lucide-react';

import {
  useAvailability,
  useBookAppointment,
  useRescheduleAppointment,
  type BookingKind,
} from '@/lib/api/hooks';
import { Icon as BrandIcon } from '@/lib/Icon';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/cn';

interface BookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When set, the dialog is in "reschedule" mode — picking a slot calls
   * `useRescheduleAppointment` against this id instead of `useBookAppointment`.
   * Title, CTA copy, and error mapping flip accordingly. Everything else
   * (slot grid, calendar, availability fetch) is identical.
   */
  rescheduleAppointmentId?: string | null;
  /** 'peptide_consult' books the dedicated evaluation visit type —
   *  distinct on the clinician's calendar. Defaults to the org's
   *  standard type. */
  kind?: BookingKind;
  /** Storefront: name the time zone the slots are shown in (the device's)
   *  above the times. */
  showTimeZone?: boolean;
}

/** The device's time zone as a reader says it ("Central Daylight Time"). */
function timeZoneLabel(at: Date): string | null {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZoneName: 'long' })
        .formatToParts(at)
        .find((part) => part.type === 'timeZoneName')?.value ?? null
    );
  } catch {
    return null;
  }
}

/**
 * BookingDialog — calendar + slot picker.
 *
 * Two views:
 *   - Calendar grid: Mon→Sun columns, dates that have availability are
 *     interactive; dates outside the visible month + dates with no slots
 *     are greyed out. Top: month label with prev/next arrows.
 *   - Slot picker: '◀ BACK TO CALENDAR' link + 4-col grid of time slots
 *     for the selected date. Picking one enables the sticky-bottom black
 *     'Confirm booking ▶' CTA.
 *
 * Availability is fetched up front (next 14 days by default) and grouped
 * by date. Month nav lets the user step forward/backward within the
 * fetched window; past months are hard-disabled.
 */
export function BookingDialog({
  open,
  onOpenChange,
  rescheduleAppointmentId = null,
  kind = 'default',
  showTimeZone = false,
}: BookingDialogProps) {
  const { data: slots = [], isLoading } = useAvailability({ days: 28, kind });
  const book = useBookAppointment();
  const reschedule = useRescheduleAppointment();
  const isReschedule = Boolean(rescheduleAppointmentId);
  const pending = book.isPending || reschedule.isPending;
  const error = (isReschedule ? reschedule.error : book.error) as
    | { message?: string }
    | null;
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  const slotsByDate = useMemo(() => {
    const m = new Map<string, typeof slots>();
    for (const s of slots) {
      const key = format(parseISO(s.start_at), 'yyyy-MM-dd');
      const arr = m.get(key) ?? [];
      arr.push(s);
      m.set(key, arr);
    }
    return m;
  }, [slots]);

  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);
  const thisMonth = startOfMonth(today);

  // Build a 6-row Mon→Sun grid for the visible month.
  const gridDays = useMemo(() => {
    const firstDay = startOfWeek(monthCursor, { weekStartsOn: 1 });
    return Array.from({ length: 42 }, (_, i) => addDays(firstDay, i));
  }, [monthCursor]);

  const slotsForSelected = selectedDate
    ? (slotsByDate.get(format(selectedDate, 'yyyy-MM-dd')) ?? [])
    : [];

  const reset = () => {
    setSelectedDate(null);
    setSelectedSlotId(null);
  };

  const handleConfirm = async () => {
    if (!selectedSlotId || pending) return;
    // Slot IDs from availability are ISO datetimes (see backend
    // appointments.py — slot.id IS the start_at iso). Both book and
    // reschedule send the same string; backend hands it to Healthie.
    if (isReschedule && rescheduleAppointmentId) {
      await reschedule.mutateAsync({
        appointmentId: rescheduleAppointmentId,
        datetime: selectedSlotId,
      });
      track('appointment_rescheduled', {
        appointment_id: rescheduleAppointmentId,
      });
    } else {
      await book.mutateAsync({
        slot_id: selectedSlotId,
        reason: kind === 'peptide_consult' ? 'Peptide consult' : 'Follow-up',
        kind,
      });
      track('appointment_booked', { slot_id: selectedSlotId, kind });
    }
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/10 backdrop-blur-[10px] z-dialog" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                     w-[min(48rem,calc(100vw-20px))] max-h-[90vh] overflow-hidden
                     bg-[var(--surface-page)] border border-line-subtle z-dialog flex flex-col"
          aria-describedby={undefined}
        >
          <header className="flex items-center justify-between bg-[var(--surface-sunken)] border-b border-line-subtle pr-2"> 
            <Dialog.Title className="font-mono uppercase text-13 text-ink-secondary px-15 py-15">
              {isReschedule ? 'RESCHEDULE APPOINTMENT' : 'BOOK APPOINTMENT'}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="w-45 h-45 flex items-center justify-center border-l border-line-subtle bg-slate text-ink-secondary hover:text-ink"
            >
              <X size={18} strokeWidth={1.5} />
            </Dialog.Close>
          </header>

          <div className="flex-1 flex flex-col overflow-y-auto">
            {!selectedDate ? (
              <CalendarView
                isLoading={isLoading}
                monthCursor={monthCursor}
                onPrev={() => {
                  // Disable navigating into past months — availability is
                  // forward-looking; previous months never have slots.
                  const prev = addMonths(monthCursor, -1);
                  if (prev >= thisMonth) setMonthCursor(prev);
                }}
                onNext={() => setMonthCursor(addMonths(monthCursor, 1))}
                canGoPrev={monthCursor > thisMonth}
                gridDays={gridDays}
                today={today}
                hasSlotsOn={(d) => (slotsByDate.get(format(d, 'yyyy-MM-dd')) ?? []).length > 0}
                onSelectDate={(d) => {
                  setSelectedDate(d);
                  setSelectedSlotId(null);
                }}
              />
            ) : (
              <SlotPicker
                date={selectedDate}
                timeZone={showTimeZone ? timeZoneLabel(selectedDate) : null}
                slots={slotsForSelected}
                selectedSlotId={selectedSlotId}
                onPickSlot={setSelectedSlotId}
                onBack={() => {
                  setSelectedDate(null);
                  setSelectedSlotId(null);
                }}
              />
            )}
          </div>

          {/* Sticky confirm CTA — visible only in slot-picker view. */}
          {selectedDate && (
            <div className="border-t border-line-subtle bg-[var(--surface-page)]">
              {error && (
                <div className="px-15 py-10 type-body-sm text-danger border-b border-line-subtle">
                  {error.message ??
                    (isReschedule ? 'Reschedule failed' : 'Booking failed')}
                </div>
              )}
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!selectedSlotId || pending}
                className="w-full flex items-center justify-between h-50 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 disabled:opacity-50"
              >
                {pending
                  ? 'Confirming…'
                  : isReschedule
                    ? 'Confirm reschedule'
                    : 'Confirm booking'}
                <span aria-hidden className="inline-flex items-center justify-center w-8"><BrandIcon name="Arrow" viewBox="0 0 10 12" /></span>
              </button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CalendarView({
  isLoading,
  monthCursor,
  onPrev,
  onNext,
  canGoPrev,
  gridDays,
  today,
  hasSlotsOn,
  onSelectDate,
}: {
  isLoading: boolean;
  monthCursor: Date;
  onPrev: () => void;
  onNext: () => void;
  canGoPrev: boolean;
  gridDays: Date[];
  today: Date;
  hasSlotsOn: (d: Date) => boolean;
  onSelectDate: (d: Date) => void;
}) {
  return (
    <div className="flex flex-col">
      {/* Month label + arrows */}
      <div className="grid grid-cols-[45px_1fr_45px] items-center border-b border-line-subtle">
        <button
          type="button"
          onClick={onPrev}
          disabled={!canGoPrev}
          aria-label="Previous month"
          className="h-45 flex items-center justify-center text-ink hover:bg-[var(--surface-sunken)] disabled:text-ink-muted disabled:hover:bg-transparent"
        >
          <ChevronLeft size={16} strokeWidth={1.5} aria-hidden />
        </button>
        <div className="text-center type-label-md text-ink">
          {format(monthCursor, 'MMM').toUpperCase()}
        </div>
        <button
          type="button"
          onClick={onNext}
          aria-label="Next month"
          className="h-45 flex items-center justify-center text-ink hover:bg-[var(--surface-sunken)]"
        >
          <span aria-hidden className="inline-flex items-center justify-center w-8"><BrandIcon name="Arrow" viewBox="0 0 10 12" /></span>
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-line-subtle">
        {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => (
          <div
            key={d}
            className="text-center type-label-xs text-ink-muted py-12"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Date grid */}
      <div className="grid grid-cols-7">
        {isLoading
          ? Array.from({ length: 42 }, (_, i) => (
              <div key={i} className="h-50 bg-[var(--surface-sunken)] opacity-50" />
            ))
          : gridDays.map((d) => {
              const inMonth = isSameMonth(d, monthCursor);
              const past = d < today;
              const available = !past && hasSlotsOn(d);
              const isToday = isSameDay(d, today);
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={!available}
                  onClick={() => onSelectDate(d)}
                  aria-label={format(d, 'MMMM d, yyyy')}
                  className={cn(
                    'h-50 flex items-center justify-center type-body-md',
                    available
                      ? 'text-ink hover:bg-[var(--surface-sunken)] cursor-pointer'
                      : 'text-ink-muted cursor-not-allowed',
                    !inMonth && 'opacity-40',
                    isToday && 'ring-1 ring-inset ring-ink',
                  )}
                >
                  {format(d, 'd')}
                </button>
              );
            })}
      </div>

      {/* Placeholder text */}
      <div className="py-30 text-center type-label-sm text-ink-muted">
        SELECT A DATE TO VIEW AVAILABILITY
      </div>
    </div>
  );
}

function SlotPicker({
  date,
  timeZone = null,
  slots,
  selectedSlotId,
  onPickSlot,
  onBack,
}: {
  date: Date;
  timeZone?: string | null;
  slots: Array<{ id: string; start_at: string }>;
  selectedSlotId: string | null;
  onPickSlot: (id: string) => void;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={onBack}
        className="self-start inline-flex items-center gap-8 px-15 py-15 type-label-sm text-ink hover:text-ink-secondary"
      >
        <span aria-hidden>◀</span> BACK TO CALENDAR
      </button>

      <div className="px-15 pt-5 pb-15 type-label-sm text-ink-muted">
        {format(date, 'EEEE · MMM d').toUpperCase()}
      </div>
      {timeZone && (
        <p className="px-15 -mt-5 pb-15 type-body-sm text-ink-secondary">
          Times are shown in {timeZone}, this device&rsquo;s time zone.
        </p>
      )}

      {slots.length === 0 ? (
        <div className="px-15 py-30 text-center type-body-md text-ink-muted">
          No times left on this day. Try another date.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-8 px-15 pb-20">
          {slots.map((s) => {
            const active = selectedSlotId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onPickSlot(s.id)}
                className={cn(
                  'h-40 type-body-sm border',
                  active
                    ? 'bg-[#5D677E] text-[var(--ink-on-inverse)] border-[#5D677E]'
                    : 'bg-[var(--surface-page)] text-ink border-line-subtle hover:border-ink',
                )}
              >
                {format(parseISO(s.start_at), 'h:mma').toLowerCase()}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
